/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, DialogTrigger, makeStyles } from "@fluentui/react-components";
import { Column } from "../common/Column";
import { Row } from "../common/Row";
import { PersonControl } from "../controls/PersonControl";
import { ReactElement } from "react";
import { Person, UserAccount, UserRef } from "../../data/types";
import { useAccountState } from "../../redux/reducers/accountReducer";
import { useDialogControls } from "./useDialogControls";

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
    stateZip: {
        width: '125px'
    },
    addContactBtn: {
        fontSize: '13px',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
    user: {
        width: '16rem',
    }
});

export interface SelectUsersDialogProps {
    title?: string;
    icon?: JSX.Element;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    onSubmit: (users: UserRef[]) => void;
    assignedUsers?: UserRef[];
    children?: ReactElement<any>;
}
export const SelectUsersDialog = (props: SelectUsersDialogProps) => {

    const { title, icon, open, onOpenChange, onSubmit, assignedUsers, children } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const { accountUsers } = primaryAccount as UserAccount ?? {};

    //State
    const [selectedUsers, setSelectedUsers] = useState<UserRef[]>([]);

    //Styles
    const styles = useStyles();

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //Logic
    const filterUsers = (user: UserRef) => {
        return !assignedUsers?.some((u) => u.userId == user.userId);
    }

    //UI Events
    const onUserSelected = (person: Person, selected: boolean) => {
        const user = person as UserRef;
        if (!!selected) {
            setSelectedUsers((prev) => { return !!prev ? [...prev, user] : [user]; });
        } else {
            const ind = selectedUsers.findIndex((x) => user.userId == x.userId);
            setSelectedUsers((prev) => { return [...prev.slice(0, ind), ...prev.slice(ind + 1)]; });
        }
    }
    const onClickSubmit = (e: any) => {
        e.stopPropagation();

        onSubmit(selectedUsers);
        clearSelectedUsers();
    }
    const onClickCancel = (e: any) => {
        e.stopPropagation();
        clearSelectedUsers();
    }
    const clearSelectedUsers = () => {
        setSelectedUsers([]);
    }

    //Effects
    useEffect(() => {

        return () => {
            setSelectedUsers([]);
        }
    }, []);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>
                {children}
            </DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} style={{ maxWidth: '750px' }}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                {icon}
                            </Column>
                            <Column>
                                {title}
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column maxHeight='500px' scroll padding='px-2 py-3'>
                            <Row gap='large'>
                                {accountUsers?.filter(filterUsers).map((userRef) => (
                                    <PersonControl
                                        key={userRef?.userId}
                                        className={styles.user}
                                        selectable
                                        onSelectionChange={onUserSelected}
                                        size='smallCard'
                                        person={userRef}
                                    />
                                ))}
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type="submit"
                                appearance="primary"
                                onClick={onClickSubmit}
                                disabled={!selectedUsers.length}
                                data-testid='select-users-dialog-submit'
                            >
                                Select Users
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                appearance="secondary"
                                onClick={onClickCancel}
                                data-testid='select-users-dialog-cancel'
                            >
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
}